import { Link } from 'react-router-dom';
import type { CreditActiveLayer, CreditMonitorAccountRow } from '@nadoutong/contracts';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { DualCurrencyAmount } from '../DualCurrencyAmount.tsx';
import { CustomerRef } from '../identity/CustomerRef.tsx';
import { usdMicrosToCnyCents } from '../formatters.ts';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import { Chip, IconButton, StatusLamp, Td, Tr } from '../../ui/index.ts';
import type { ChipTone, LampTone } from '../../ui/index.ts';
import { cx } from '../../ui/cx.ts';
import { CreditLayerUsageBar } from './CreditLayerUsageBar.tsx';

/**
 * One credit-monitor row (router#2014). Long-term and temporary grants
 * share this shape. Overdue rows are red and have no dismiss control.
 */

const LAYER_LAMP: Record<CreditActiveLayer, LampTone> = {
  prepaid: 'ok',
  long_term: 'warn',
  temporary: 'warn',
  over_limit: 'bad',
};

const STAGE_LABEL: Record<string, I18nKey> = {
  frozen: 'credit.overdue.stage.frozen',
  stop_notice: 'credit.overdue.stage.stop_notice',
  stopped: 'credit.overdue.stage.stopped',
  collections: 'credit.overdue.stage.collections',
};

function stageTone(stage: string): ChipTone {
  if (stage === 'stopped' || stage === 'collections') return 'bad';
  return 'warn';
}

function MonitorMoney({
  usdMicros,
  cnyPerUsd,
}: {
  readonly usdMicros: number;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  return (
    <DualCurrencyAmount
      display="cny"
      cnyCents={usdMicrosToCnyCents(usdMicros, cnyPerUsd)}
      signed={false}
      align="start"
    />
  );
}

function LayerStatus({
  row,
  cnyPerUsd,
}: {
  readonly row: CreditMonitorAccountRow;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const layers = row.layers;
  const money = (usdMicros: number): JSX.Element => (
    <MonitorMoney usdMicros={usdMicros} cnyPerUsd={cnyPerUsd} />
  );
  if (layers.active_layer === 'over_limit') {
    return (
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span>{t('credit.overview.layer.over_limit')}</span>
        {money(layers.over_limit_usd_micros)}
        {money(row.live_debt_usd_micros)}
      </span>
    );
  }
  if (layers.active_layer === 'temporary') {
    return (
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span>{t('credit.overview.layer.temporary')}</span>
        {money(layers.temporary_used_usd_micros)}
        <span className="text-paper-low">/</span>
        {money(layers.temporary_cap_usd_micros)}
      </span>
    );
  }
  if (layers.active_layer === 'long_term') {
    return (
      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span>{t('credit.overview.layer.long_term')}</span>
        {money(layers.long_term_used_usd_micros)}
        <span className="text-paper-low">/</span>
        {money(layers.long_term_cap_usd_micros)}
      </span>
    );
  }
  return (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span>{t('credit.overview.layer.prepaid')}</span>
      {money(layers.prepaid_remaining_usd_micros)}
    </span>
  );
}

function NextDue({
  row,
  cnyPerUsd,
}: {
  readonly row: CreditMonitorAccountRow;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  if (row.overdue !== null) {
    return <>{t('credit.monitor.next_due.overdue', { date: row.overdue.due_date })}</>;
  }
  if (row.due_soon !== null) {
    return (
      <span className="flex flex-wrap items-center gap-2">
        <span>{t('credit.monitor.next_due.due', { date: row.due_soon.due_date })}</span>
        <MonitorMoney usdMicros={row.live_debt_usd_micros} cnyPerUsd={cnyPerUsd} />
      </span>
    );
  }
  if (row.temporary_expires_on !== null) {
    return <>{t('credit.monitor.next_due.temporary', { date: row.temporary_expires_on })}</>;
  }
  return <>{t('credit.overview.dash')}</>;
}

export interface CreditMonitorRowProps {
  readonly row: CreditMonitorAccountRow;
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly cnyPerUsd: number | null;
}

export function CreditMonitorRow({
  row,
  expanded,
  onToggle,
  cnyPerUsd,
}: CreditMonitorRowProps): JSX.Element {
  const t = useT();
  const overdue = row.overdue !== null;
  const canExpand = row.org_id !== null;
  const label = row.org_name ?? row.org_id ?? row.account_id;

  return (
    <Tr
      interactive={false}
      data-testid={`credit-overdue-row-${row.account_id}`}
      data-queue={row.queue}
      data-overdue={overdue ? 'true' : 'false'}
      className={overdue ? 'bg-bad-dim hover:bg-bad-dim' : undefined}
    >
      <Td>
        <div className="flex items-center gap-2">
          {canExpand ? (
            <IconButton
              size="sm"
              label={expanded ? t('credit.monitor.collapse') : t('credit.monitor.expand')}
              onClick={onToggle}
              aria-expanded={expanded}
              data-testid={`credit-monitor-expand-${row.account_id}`}
            >
              {expanded ? (
                <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
              ) : (
                <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
              )}
            </IconButton>
          ) : null}
          {row.org_id !== null ? (
            <Link
              className="text-accent hover:underline"
              to={`/crm/customers/${encodeURIComponent(row.org_id)}`}
              data-testid={`credit-overdue-org-${row.org_id}`}
            >
              {label}
            </Link>
          ) : (
            <span className="font-mono text-micro">
              <CustomerRef accountId={row.account_id} />
            </span>
          )}
        </div>
      </Td>
      <Td>
        <CreditLayerUsageBar
          layers={row.layers}
          cnyPerUsd={cnyPerUsd}
          testId={`credit-monitor-bar-${row.account_id}`}
        />
      </Td>
      <Td>
        <div className="flex flex-col gap-1.5">
          {overdue && row.overdue !== null ? (
            <span
              className={cx('flex flex-wrap items-center gap-x-2 gap-y-1 font-medium text-bad')}
              data-testid={`credit-monitor-overdue-status-${row.account_id}`}
            >
              <StatusLamp tone="bad" />
              <span>
                {t('credit.monitor.status.overdue', {
                  days: String(row.overdue.days_past_due),
                })}
              </span>
              <MonitorMoney usdMicros={row.live_debt_usd_micros} cnyPerUsd={cnyPerUsd} />
            </span>
          ) : (
            <span className="flex items-center gap-1.5" data-layer={row.layers.active_layer}>
              <StatusLamp tone={LAYER_LAMP[row.layers.active_layer]} />
              <LayerStatus row={row} cnyPerUsd={cnyPerUsd} />
            </span>
          )}
          {row.overdue?.stage !== null && row.overdue?.stage !== undefined ? (
            <Chip tone={stageTone(row.overdue.stage)}>
              {t(STAGE_LABEL[row.overdue.stage] ?? 'credit.overdue.stage.frozen')}
            </Chip>
          ) : null}
          {row.exemption !== null ? (
            <Chip tone="warn" testId={`credit-overdue-exemption-${row.account_id}`}>
              {t('credit.exemption.chip', {
                days: String(row.exemption.remaining_days),
              })}
            </Chip>
          ) : null}
        </div>
      </Td>
      <Td data-testid={`credit-monitor-next-due-${row.account_id}`}>
        <NextDue row={row} cnyPerUsd={cnyPerUsd} />
      </Td>
    </Tr>
  );
}
